<template>
    <div v-if="waterSiteVisible">
        <h4>Site {{ index + 1 }}</h4>

        <div class="form-group" :class="{ 'form-group--error': $v.value.spid.$error || value.quoteToolUsable == false || validSpid == false }">
            <label :for="'spid' + index">{{ labels.stepTwoFindSiteSPIDLabel }}*</label>
            <div class="row">
                <div class="col-xs-12 col-sm-8">
                    <input @input="updateValue('spidLookup', $event.target.value)"
                           :value="value.spidLookup"
                           type="text"
                           maxlength="13"
                           class="form-control"
                           :id="'spid' + index"
                           placeholder="This can be found on your bill">
                    <div class="error" v-if="!$v.value.spid.required">Valid spid number is required.</div>
                </div>               
                <div class="col-xs-12 col-sm-4">  
                    <button :disabled="isSpidButtonDisabled" @click.prevent="findSPID(index)" class="u-mt-0 u-mb-0 u-ptb-10 btn--full-width">{{ labels.stepTwoFindSiteButtonText }}</button>
                </div>
            </div>
            <p v-if="fetchingSPID">Loading...</p>  
            <transition name="fade"> 
                <div v-if="value.spid && !fetchingSPID">
                    <div v-if="value.quoteToolUsable == false" class="error">
                        Because this site has multiple water meters, we won't be able to provide you with an instant quote online. If you choose to continue, we'll be in touch to provide a quote using the information provided.
                    </div> 
                    <div v-else>
                        <address>
                            {{ value.address.street }}<br />
                            {{ value.address.town }}<br />
                            {{ value.address.country }}
                            {{ value.address.postcode }}
                        </address>

                        <p>
                            Estimated usage at this site - <strong>{{ value.usage }} m<sup>3</sup> {{ value.frequency }}</strong>
                        </p>

                        <div class="form-group" :class="{ 'form-group--error': $v.value.usage.$error }">
                            <div class="row">
                                <div class="col-xs-12">
                                    <label :for="'usage' + index">{{ labels.stepTwoYourWaterUsageLabel }}*</label>
                                </div>
                                <div class="col-xs-12 col-sm-8 custom-number-field">
                                    <input @keydown.enter.prevent.self 
                                           @input="updateValue('usage', $event.target.value)"
                                           :value="value.usage"
                                           type="text"
                                           maxlength="7"
                                           class="form-control custom-number-field--mCubed"
                                           :id="'usage' + index">
                                </div>

                                <div class="form-group" :class="{ 'form-group--error': $v.value.frequency.$error }">
                                    <div class="col-xs-12 col-sm-4">
                                        <div class="custom-select">
                                            <select @input="updateValue('frequency', $event.target.value)" :value="value.frequency" class="form-control">
                                                <option value="annually">Annually</option>
                                                <option value="half-yearly">Half-yearly</option>
                                                <option value="quarterly">Quarterly</option>
                                                <option value="monthly">Monthly</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="error" v-if="!$v.value.usage.required || !$v.value.usage.numeric || !$v.value.usage.isPositive">
                            Valid water usage amount is required.
                        </div>
                         
                        <div class="error" v-if="!$v.value.frequency.required">
                            Frequency is required.
                        </div>

                        <div v-if="hasReferenceNumber">
                            <div class="form-group" :class="{ 'form-group--error': $v.value.customerReferenceNumber.$error }">
                                <label :for="'referenceNum' + index">{{ labels.stepTwoCustomerReferenceNumberLabel }}*</label>
                                <input @keydown.enter.prevent.self
                                       @input="updateValue('customerReferenceNumber', $event.target.value)"
                                       :value="value.customerReferenceNumber"
                                       type="number"
                                       class="form-control"
                                       :id="'referenceNum' + index"
                                       :placeholder="labels.stepTwoCustomerReferenceNumberPlaceholder">
                            </div>

                            <div class="error" v-if="!$v.value.customerReferenceNumber.required 
                                 || !$v.value.customerReferenceNumber.numeric 
                                 || !$v.value.customerReferenceNumber.minLength
                                 || !$v.value.customerReferenceNumber.maxLength">
                                {{ labels.stepTwoCustomerReferenceNumberValidationMsg }}
                            </div>
                        </div>

                        <!-- billing address -->
                        <div class="checkbox">
                            <label>
                                <input type="checkbox"
                                       v-model="isBillingSite"
                                       >
                                {{ labels.stepTwoUseSiteBillingCheckboxLabel }}
                                <span class="checkmark"></span>
                            </label>
                        </div>
                    </div>
                </div> 
            </transition>
        </div>
    </div>
</template>

<script>
    import { mapActions, mapGetters, mapState } from 'vuex';
    import { required, numeric, maxLength, minLength } from 'vuelidate/lib/validators';

    export default {
        props: {
            index: {
                type: Number,
                required: true,
            },

            value: {
                type: Object,
                required: true
            },

            hasReferenceNumber: {
                type: Boolean,
                required: true,
            }
        },

        data() {
            return {
                isBillingSite: this.value.isBillingSite,  
                fetchingSPID: false,
                validSpid: null
            }
        },

        computed: {
            ...mapGetters('waterConsumptionStep', [
                'continueQuoteToolJourney'
            ]),

            ...mapState('microcopy', [
                'labels',
            ]),

            isSpidButtonDisabled() {
                return (this.value.spidLookup == null || this.value.spidLookup == '' || this.fetchingSPID) ? true : false;   
            },

            waterSiteVisible() {
                if (!this.continueQuoteToolJourney) {
                    if (this.value.quoteToolUsable == true || this.value.quoteToolUsable == null) {
                        return false;
                    } else {
                        return true; 
                    }
                }

                return true;
            }
        },

        methods: {
            updateValue(key, value) {
                this.$emit(
                    'input',
                    {
                        site: {
                            ...this.value,
                            [key]: value
                        },
                        index: this.index
                    });
            },

            findSPID(index) {
                this.fetchingSPID = true;
                this.fetchSPID(index)
                    .then(data => { this.validSpid = true; })
                    .catch(err => { this.validSpid = false; })
                    .finally(() => { this.fetchingSPID = false; }); 
            }, 

            ...mapActions('waterConsumptionStep', [
                'fetchSPID'
            ]),
        },

        watch: {
            isBillingSite: function (val) {
                this.updateValue('isBillingSite', val);
            },

            'value.isBillingSite': function (val) {
                this.isBillingSite = val; 
            }
        }, 

        validations() {
            if (this.hasReferenceNumber) {
                return {
                    value: {
                        spid: {
                            required,
                        },
                        usage: {
                            required,
                            numeric,
                            isPositive: function (value) {
                                return value > 0;
                            },
                        }, 
                        customerReferenceNumber: {
                            required,
                            numeric,
                            maxLength: maxLength(10),
                            minLength: minLength(10)
                        }, 
                        frequency: {
                            required,
                        } 
                    }
                }
            } else {
                return {
                    value: {
                        spid: {
                            required,
                        },
                        usage: {
                            required,
                            numeric,
                            isPositive: function (value) {
                                return value > 0;
                            },
                        },
                        frequency: {
                            required,
                        }
                    }
                }
            }
        }
    }
</script>